import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Mail, Lock, Loader2, ArrowLeft } from "lucide-react";
import { apiFetch, adminFetch, saveAdminToken, clearAdminToken, saveAuthToken } from "@/lib/api";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [view, setView] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");

  const login = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setErrorText("Inserisci email e password.");
      return;
    }
    setLoading(true);
    setErrorText("");
    try {
      const result = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      saveAdminToken(result.token);
      saveAuthToken(result.token);
      const me = await adminFetch("/auth/me");
      if (!me.is_admin) {
        clearAdminToken();
        setErrorText("Accesso riservato agli amministratori di CargoFlow.");
        return;
      }
      navigate("/admin", { replace: true });
    } catch (error) {
      clearAdminToken();
      setErrorText(error.message || "Controlla le credenziali.");
    } finally {
      setLoading(false);
    }
  };

  const sendForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      setErrorText("Inserisci l'email amministratore.");
      return;
    }
    setLoading(true);
    setErrorText("");
    setForgotMsg("");
    try {
      const result = await apiFetch("/auth/admin/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email: forgotEmail.trim(), origin: window.location.origin }),
      });
      setForgotMsg(result.message || "Se l'email corrisponde a un account amministratore, riceverai le istruzioni.");
    } catch (error) {
      setErrorText(error.message || "Operazione non riuscita. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const goForgot = () => {
    setView("forgot");
    setErrorText("");
    setForgotMsg("");
    setForgotEmail(email);
  };
  const goLogin = () => {
    setView("login");
    setErrorText("");
    setForgotMsg("");
  };

  const inputWrap = "flex min-h-[50px] items-center gap-2.5 rounded-xl border border-border bg-secondary/40 px-4";

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5 py-10">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-border bg-card shadow-lg">
        <div className="flex flex-col items-center gap-2 bg-primary px-6 py-8 text-primary-foreground">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/20">
            <ShieldCheck className="h-7 w-7" />
          </span>
          <p className="text-2xl font-extrabold tracking-tight">CargoFlow Admin</p>
          <p className="text-[13px] text-primary-foreground/85">Pannello di controllo piattaforma</p>
        </div>

        {view === "login" ? (
          <form onSubmit={login} className="flex flex-col gap-2 p-6">
            <label className="mt-1 text-[13px] font-bold text-card-foreground">Email amministratore</label>
            <div className={inputWrap}>
              <Mail className="h-5 w-5 text-muted-foreground" />
              <input
                data-testid="admin-login-email"
                type="email"
                autoCapitalize="none"
                placeholder="admin@cargoflow.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 flex-1 bg-transparent text-[15px] text-foreground outline-none"
              />
            </div>
            <label className="mt-1 text-[13px] font-bold text-card-foreground">Password</label>
            <div className={inputWrap}>
              <Lock className="h-5 w-5 text-muted-foreground" />
              <input
                data-testid="admin-login-password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 flex-1 bg-transparent text-[15px] text-foreground outline-none"
              />
            </div>
            <button
              type="button"
              data-testid="admin-forgot-link"
              onClick={goForgot}
              className="mt-1 self-start text-[13px] font-bold text-primary hover:underline"
            >
              Password dimenticata?
            </button>
            {errorText ? (
              <p data-testid="admin-login-error" className="mt-1 text-[13px] font-semibold text-destructive">
                {errorText}
              </p>
            ) : null}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="mt-3 flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold tracking-wide text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              {loading ? "Accesso..." : "ACCEDI AL PANNELLO"}
            </button>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Area riservata: gli account utente standard non possono accedere.
            </p>
          </form>
        ) : (
          <form onSubmit={sendForgot} className="flex flex-col gap-2 p-6">
            <button
              type="button"
              data-testid="admin-forgot-back"
              onClick={goLogin}
              className="mb-1 flex items-center gap-1.5 self-start text-[13px] font-bold text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Torna al login
            </button>
            <p className="text-[15px] font-extrabold text-card-foreground">Recupera l&apos;accesso amministratore</p>
            <p className="text-[13px] text-muted-foreground">
              Inserisci l&apos;email del tuo account admin: riceverai un link sicuro e temporaneo per reimpostare la password.
            </p>
            <label className="mt-2 text-[13px] font-bold text-card-foreground">Email amministratore</label>
            <div className={inputWrap}>
              <Mail className="h-5 w-5 text-muted-foreground" />
              <input
                data-testid="admin-forgot-email"
                type="email"
                autoCapitalize="none"
                placeholder="admin@cargoflow.it"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="h-12 flex-1 bg-transparent text-[15px] text-foreground outline-none"
              />
            </div>
            {forgotMsg ? (
              <p data-testid="admin-forgot-message" className="mt-1 text-[13px] font-semibold text-[hsl(var(--success))]">
                {forgotMsg}
              </p>
            ) : null}
            {errorText ? (
              <p data-testid="admin-forgot-error" className="mt-1 text-[13px] font-semibold text-destructive">
                {errorText}
              </p>
            ) : null}
            <button
              type="submit"
              data-testid="admin-forgot-submit"
              disabled={loading || !!forgotMsg}
              className="mt-3 flex min-h-[52px] items-center justify-center gap-2 rounded-xl bg-primary text-sm font-extrabold tracking-wide text-primary-foreground hover:opacity-90 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              {loading ? "Invio..." : "INVIA LINK DI RESET"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
